import { useCallback, useState } from "react";

import { PDFDict, PDFDocument, PDFName } from 'pdf-lib';



type PdfType = {
  isXFA: boolean;
  isAcro: boolean;
  isDocx: boolean;
  type: "XFA_PDF" | "ACROFORM_PDF" | "NO_FORM" | "DOCX";
};

// Case-insensitive ASCII byte search without full UTF-8 decode
function indexOfAsciiCI(haystack: Uint8Array, needle: string): number {
  const n = needle.toLowerCase();
  const len = n.length;
  if (len === 0) return 0;
  outer: for (let i = 0; i <= haystack.length - len; i++) {
    for (let j = 0; j < len; j++) {
      const b = haystack[i + j];
      const lower = b >= 65 && b <= 90 ? b + 32 : b;
      if (lower !== n.charCodeAt(j)) continue outer;
    }
    return i;
  }
  return -1;
}

function hasXfaMarkers(bytes: Uint8Array): boolean {
  // Look for XFA-specific XML markers and patterns
  const xfaSpecificPatterns = [
    "<xdp:xdp",
    "<xfa:",
    "xmlns:xfa",
    "xfa:datasets",
    "xfa:template",
    "/template/subform",  // XFA template references
    "xmlns:desc=\"http://ns.adobe.com/xfa/",  // XFA namespace
  ];
  return xfaSpecificPatterns.some((p) => indexOfAsciiCI(bytes, p) !== -1);
}
function isDocxOrDocFile(bytes: Uint8Array, fileName: string): boolean {
  // Check file extension first
  const lowerName = fileName.toLowerCase();
  const hasDocExtension = lowerName.endsWith('.docx') || lowerName.endsWith('.doc');
  
  if (!hasDocExtension) return false;
  
  // DOCX files are ZIP archives (signature: 50 4B 03 04)
  const isZipSignature = bytes.length >= 4 && 
    bytes[0] === 0x50 && bytes[1] === 0x4B && 
    bytes[2] === 0x03 && bytes[3] === 0x04;
  
  // DOC files are Microsoft Office binary format (signature: D0 CF 11 E0)
  const isDocSignature = bytes.length >= 4 && 
    bytes[0] === 0xD0 && bytes[1] === 0xCF && 
    bytes[2] === 0x11 && bytes[3] === 0xE0;
  
  return isZipSignature || isDocSignature;
}


export function useDetectPdfType() {
  const [pdfType, setPdfType] = useState<PdfType | null>(null);

  const detect = useCallback(async (file: File): Promise<PdfType> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      
      // Check if it's a DOCX/DOC file first
      const isDocxFile = isDocxOrDocFile(bytes, file.name);
      if (isDocxFile) {
        const result: PdfType = { isXFA: false, isAcro: false, isDocx: true, type: "DOCX" };
        setPdfType(result);
        return result;
      }
      
      const pdfDoc = await PDFDocument.load(arrayBuffer, { 
        ignoreEncryption: true,
        updateMetadata: false 
      });

      const catalog = pdfDoc.context.lookup(pdfDoc.context.trailerInfo.Root);
      
      if (!catalog || !(catalog instanceof PDFDict)) {
        const result: PdfType = { isXFA: false, isAcro: false, isDocx: false, type: "NO_FORM" };
        setPdfType(result);
        return result;
      }

      // Check for AcroForm in the catalog
      const acroForm = catalog.lookup(PDFName.of('AcroForm'));
      
      if (!acroForm) {
        const result: PdfType = { isXFA: false, isAcro: false, isDocx: false, type: "NO_FORM" };
        setPdfType(result);
        console.log('PDF Detection Result: No form found');
        return result;
      }

      // Check if AcroForm is a dictionary
      if (!(acroForm instanceof PDFDict)) {
        const result: PdfType = { isXFA: false, isAcro: false, isDocx: false, type: "NO_FORM" };
        setPdfType(result);
        return result;
      }

      // Check for XFA in AcroForm dictionary
      const xfaEntry = acroForm.lookup(PDFName.of('XFA'));
      const hasXFAEntry = xfaEntry !== undefined;

      // Check for Fields in AcroForm dictionary
      const fieldsEntry = acroForm.lookup(PDFName.of('Fields'));
      const hasFields = fieldsEntry !== undefined;

      // Additional check: scan raw bytes for XFA XML markers
      const hasXfaXmlMarkers = hasXfaMarkers(bytes);

      // More detailed inspection
      let fieldCount = 0;
      try {
        const form = pdfDoc.getForm();
        const fields = form.getFields();
        fieldCount = fields.length;
      } catch (e) {
        console.log('Could not get form fields:', e);
      }

      console.log('Detection details:', {
        hasXFAEntry: hasXFAEntry,
        hasFields: hasFields,
        hasXfaXmlMarkers: hasXfaXmlMarkers,
        fieldCount: fieldCount,
        xfaEntryType: xfaEntry?.constructor?.name,
        fieldsEntryType: fieldsEntry?.constructor?.name,
        fileName: file.name
      });

      let result: PdfType;

      // Decision logic:
      // 1. XFA forms have /XFA entry AND (XFA XML content OR fieldCount = 0)
      // 2. AcroForms have /Fields array with actual fields (fieldCount > 0)
      // 3. Hybrid forms have both - we prioritize by what's actually usable
      
      if (hasXFAEntry) {
        // Has XFA entry in dictionary
        if (hasXfaXmlMarkers || fieldCount === 0) {
          // It's XFA if it has XFA XML markers OR if there are no fillable fields
          if (fieldCount > 0) {
            // Hybrid: has both XFA and fillable fields - prefer AcroForm
            result = { isXFA: false, isAcro: true, isDocx: false, type: "ACROFORM_PDF" };
          } else {
            // Pure XFA: has XFA but no fillable fields
            result = { isXFA: true, isAcro: false, isDocx: false, type: "XFA_PDF" };
          }
        } else {
          // Has XFA entry but no XFA markers and has fields - treat as AcroForm
          result = { isXFA: false, isAcro: true, isDocx: false, type: "ACROFORM_PDF" };
        }
      } else if (hasFields && fieldCount > 0) {
        // Has Fields and actual field count - pure AcroForm
        result = { isXFA: false, isAcro: true, isDocx: false, type: "ACROFORM_PDF" };
        
      } else {
        // Has AcroForm entry but no usable fields or XFA
        result = { isXFA: false, isAcro: true, isDocx: false, type: "NO_FORM" };
      }

      setPdfType(result);
      return result;

    } catch (error) {
      // Fallback to binary parsing if pdf-lib fails
      const result = await detectWithBinaryParsing(file);
      setPdfType(result);
      return result;
    }
  }, []);

  return { pdfType, detect };
}




// Fallback method using binary parsing
async function detectWithBinaryParsing(file: File): Promise<PdfType> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        
        // Convert to string for searching
        let pdfString = '';
        const searchLength = Math.min(uint8Array.length, 100000); // Search first 100KB
        
        for (let i = 0; i < searchLength; i++) {
          pdfString += String.fromCharCode(uint8Array[i]);
        }
        
        // Look for AcroForm entry
        const acroFormMatch = /\/AcroForm\s*<<[^>]*>>/gs.exec(pdfString);
        
        if (!acroFormMatch) {
          // No form found
          resolve({ isXFA: false, isAcro: false, isDocx: false, type: "NO_FORM" });
          return;
        }
        
        // Get the AcroForm section
        const acroFormSection = acroFormMatch[0];
        
        // More specific XFA detection - look for /XFA followed by array or stream
        const hasXFA = /\/XFA\s*[\[\(]/.test(acroFormSection) || 
                       /\/XFA\s*\d+\s+\d+\s+R/.test(acroFormSection);
        
        if (hasXFA) {
          resolve({ isXFA: true, isAcro: false, isDocx: false, type: "XFA_PDF" });
        } else {
          resolve({ isXFA: false, isAcro: true, isDocx: false, type: "ACROFORM_PDF" });
        }
        
      } catch (error) {
        console.error('Binary detection failed:', error);
        // Default to AcroForm on error
        resolve({ isXFA: false, isAcro: true, isDocx: false, type: "ACROFORM_PDF" });
      }
    };
    
    reader.onerror = () => {
      resolve({ isXFA: false, isAcro: true, isDocx: false, type: "ACROFORM_PDF" });
    };
    
    reader.readAsArrayBuffer(file);
  });
}


